<template>
    <div>
        <v-dialog
            v-model="display"
            width="1300"
            persistent
            scrollable>
            <v-card>
                <v-card-title>
                    {{ $t('sales_order_title') }}
                    <v-spacer></v-spacer>
                    <v-btn v-if="!loading.data" @click="openSalesOrderPrintDialog(salesOrder.id)" icon class="mx-1">
                        <v-icon>mdi-printer</v-icon>
                    </v-btn>
                    <v-menu
                        offset-y>
                        <template v-slot:activator="{on}">
                            <v-btn
                                icon
                                v-on="on"
                                class="mx-1">
                                <v-icon>mdi-dots-vertical</v-icon>
                            </v-btn>
                        </template>
                        <v-list width="250" dense>
                            <v-list-item
                                v-if="(salesOrder.meta.activatable && authorizations.sales_orders) ? authorizations.sales_orders.create : false"
                                link
                                @click="openDecisionDialog({title: $t('confirm_title'), content: $t('are_you_sure_reopen_salesOrder_action'), target: 'reopen_salesOrder_action'})">
                                <v-list-item-icon>
                                    <v-icon>mdi-circle</v-icon>
                                </v-list-item-icon>
                                <v-list-item-title>{{ $t('activate') }}</v-list-item-title>
                            </v-list-item>
                            <v-list-item
                                v-if="(salesOrder.meta.extendable  && authorizations.sales_orders) ? authorizations.sales_orders.create : false"
                                link
                                @click="openDecisionDialog({title: $t('confirm_title'), content: $t('are_you_sure_extend_validity'), target: 'extend_validity'})">
                                <v-list-item-icon>
                                    <v-icon>mdi-circle</v-icon>
                                </v-list-item-icon>
                                <v-list-item-title>{{ $t('extend_validity') }}</v-list-item-title>
                            </v-list-item>
                            <v-list-item
                                v-if="(salesOrder.meta.extendable && authorizations.sales_orders) ? authorizations.sales_orders.create : false"
                                link
                                @click="openDecisionDialog({title: $t('confirm_title'), content: $t('are_you_sure_extend_sales_order'), target: 'extend_sales_order'})">
                                <v-list-item-icon>
                                    <v-icon>mdi-circle</v-icon>
                                </v-list-item-icon>
                                <v-list-item-title>{{ $t('extend_sales_order') }}</v-list-item-title>
                            </v-list-item>
                            <v-list-item
                                v-if="(salesOrder.meta.is_releasable && authorizations.sales_orders) ? authorizations.sales_orders.create : false"
                                link
                                @click="openDecisionDialog({title: $t('confirm_title'), content: $t('are_you_sure_release_salesOrder_action'), target: 'release_salesOrder_action'}) ">
                                <v-list-item-icon>
                                    <v-icon>mdi-circle</v-icon>
                                </v-list-item-icon>
                                <v-list-item-title>{{ $t('release_blanket_agreement') }}</v-list-item-title>
                            </v-list-item>
                            <v-list-item
                                @click="openSalesOrderDialog({ editMode: true, id: salesOrder.id })"
                                v-if="authorizations.sales_orders ? authorizations.sales_orders.update : false"
                                :disabled="!salesOrder.meta.editable"
                                link>
                                <v-list-item-icon>
                                    <v-icon  :disabled="!salesOrder.meta.editable">mdi-pencil</v-icon>
                                </v-list-item-icon>
                                <v-list-item-title>{{ $t('edit_sales_order') }}</v-list-item-title>
                            </v-list-item>
                            <v-list-item
                                v-if="authorizations.sales_orders ? authorizations.sales_orders.delete : false"
                                :disabled="!salesOrder.meta.deletable"
                                @click="openDeleteDialog({target: 'salesOrder', id: salesOrder.id})"
                                link>
                                <v-list-item-icon>
                                    <v-icon :disabled="!salesOrder.meta.deletable">mdi-delete</v-icon>
                                </v-list-item-icon>
                                <v-list-item-title>{{ $t('delete_sales_order') }}</v-list-item-title>
                            </v-list-item>
                        </v-list>
                    </v-menu>
                </v-card-title>
                <div class="text-center" v-if="loading.data">
                    <v-progress-circular
                        indeterminate>
                    </v-progress-circular>
                </div>
                <v-card-text v-else>
                    <v-subheader>{{ $t('title_info') }}</v-subheader>
                    <v-simple-table v-if="salesOrder" dense>
                        <tbody>
                            <tr>
                                <th>{{ $t('code') }}</th>
                                <td colspan="3">{{ salesOrder.code }}</td>
                            </tr>
                            <tr>
                                <th>{{ $t('date') }}</th>
                                <td>{{ salesOrder.date }} </td>
                                <th>{{  $t('validity_date') }}</th>
                                <td>{{ salesOrder.validity_date }} </td>
                            </tr>
                            <tr>
                                <th>{{ $t('customer') }} </th>
                                <td>{{ salesOrder.customer }}</td>
                                <th>{{ $t('code') }}</th>
                                <td>{{ salesOrder.customer_code }}</td>
                            </tr>
                        </tbody>
                    </v-simple-table>
                    <v-subheader>{{ $t('sales_order_line') }}</v-subheader>
                    <v-data-table
                        v-model="selected.lines"
                        dense
                        :items="salesOrder.sales_order_item_lines"
                        :headers="headers.salesOrderLines"
                        disable-sort
                        disable-pagination
                        hide-default-footer
                        :show-select="selectable"
                        single-select>
                        <template v-slot:[`item.quantity`]="{item}">
                            {{ formatNumber(item.quantity, 2) }} {{item.measure_unit}}
                        </template>
                        <template v-slot:[`item.price`]="{item}">
                            {{ formatNumber(item.price, 2) }}
                        </template>
                        <template v-slot:[`item.total`]="{item}">
                            {{ formatPrice(item.total) }}
                        </template>
                        <template v-slot:[`item.subtotal`]="{item}">
                            {{ formatNumber(item.subtotal) }}
                        </template>
                        <template v-slot:[`item.tax`]="{item}">
                            {{ formatNumber(item.tax) }}
                        </template>
                        <template slot="body.append">
                            <tr>
                                <td colspan="5"></td>
                                <td><b>{{ $t('sub_total') }}</b></td>
                                <td><b>{{ subtotal }}</b></td>
                                <td colspan="4"></td>
                            </tr>
                            <tr>
                                <td colspan="5"></td>
                                <td>{{ $t('tax') }}</td>
                                <td>{{ tax }}</td>
                                <td colspan="4"></td>
                            </tr>
                            <tr>
                                <td colspan="5"></td>
                                <td><b>{{ $t('total') }}</b></td>
                                <td><b>{{ total }}</b></td>
                                <td colspan="4"></td>
                            </tr>
                        </template>
                    </v-data-table>
                    <v-subheader>Notes</v-subheader>
                    <v-sheet>
                        {{salesOrder.notes}}
                    </v-sheet>
                    <v-subheader>{{ $t('payment_details') }}</v-subheader>
                    <v-simple-table v-if="salesOrder">
                        <tbody>
                            <tr>
                                <th>{{ $t('customer_balance') }}:</th>
                                <td>{{ formatPrice(salesOrder.customer_balance) }}</td>
                                <th>{{ $t('payment_to_order') }}:</th>
                                <td>{{ formatPrice(salesOrder.payment_required) }}</td>
                            </tr>
                        </tbody>
                    </v-simple-table>
                    <v-simple-table>
                        <thead>
                            <tr>
                                <th>{{ $t("created_by") }}</th>
                                <th>{{ $t("created_at") }}</th>
                                <th>{{ $t("updated_by")}}</th>
                                <th>{{ $t("updated_at") }}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>{{ salesOrder.created_by }}</td>
                                <td>{{ moment(salesOrder.created_at).format("YYYY-MM-DD HH:MM:SS") }}</td>
                                <td>{{ salesOrder.updated_by }}</td>
                                <td>{{ moment(salesOrder.updated_at).format("YYYY-MM-DD HH:MM:SS") }}</td>
                            </tr>
                        </tbody>
                    </v-simple-table>
                    <v-expansion-panels v-if="salesOrder" class="my-2">
                        <v-expansion-panel>
                            <v-expansion-panel-header>{{$t("activities")}}</v-expansion-panel-header>
                            <v-expansion-panel-content v-if="salesOrder.activities.length > 0">
                                <v-simple-table dense>
                                    <thead>
                                        <tr>
                                            <th>{{ $t("timestamp") }}</th>
                                            <th>{{ $t("user") }}</th>
                                            <th>{{ $t("title") }}</th>
                                            <th>{{ $t("content") }}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="activity in salesOrder.activities" :key="activity.id">
                                            <td>{{activity.timestamp}}</td>
                                            <td>{{activity.user}}</td>
                                            <td>{{activity.title}}</td>
                                            <td>{{activity.content}}</td>
                                        </tr>
                                    </tbody>
                                </v-simple-table>
                            </v-expansion-panel-content>
                        </v-expansion-panel>
                    </v-expansion-panels>
                </v-card-text>
                <v-card-actions>
                    <v-btn text 
                        @click="emitSelect"
                        :disabled="hasNoSelectedLines"
                        v-if="selectable">
                        {{ $t("add_btn") }}
                    </v-btn>
                    <v-btn text @click="close">
                        {{ $t('close_btn') }}
                    </v-btn>
                </v-card-actions>
            </v-card>
            
        </v-dialog>
        <RetryDialog
            ref="retryDialog"
            @submit="onRetryDialogSubmit"/>
        <SalesOrderPrintDialog
            ref="salesOrderPrintDialog"/>
        <AlertDialog
            ref="alertDialog"/>
        <SalesOrderExtendDialog
            ref="salesOrderExtendDialog"
            @submit="onSalesOrderExtendDialogSubmit"/>
        <SalesOrderDialog
            ref="salesOrderDialog"
            @submit="onSalesOrderDialogSubmit"/>
        <DeleteSingleDialog
            @submit="onDeleteDialogSubmit"
            ref="deleteSingleDialog"/>
        <LoadingDialog
            :display="loadingDialog.display"
            :message="loadingDialog.message"/>
        <decision-dialog
            ref="decisionDialog"
            @submit="decisionDialogHandler">
        </decision-dialog>
    </div>
</template>
<script>
import RetryDialog from '@/components/common/RetryDialog';
import SalesOrderPrintDialog from '@/components/Sales/SalesOrderPrintDialog';
import { mapState, mapActions } from 'vuex';
import {formatMoney, formatNumber} from 'accounting';
import AlertDialog from '../common/AlertDialog';
import SalesOrderExtendDialog from '../Sales/SalesOrderExtendDialog';
import moment from 'moment';
import SalesOrderDialog from '../Sales/SalesOrderDialog';
import DeleteSingleDialog from '../common/DeleteSingleDialog';
import LoadingDialog from '../common/LoadingDialog';
import DecisionDialog from '../common/DecisionDialog.vue';

export default {
    name: "SalesOrderDisplayDialog",
    props: {
        selectable: {
            type: Boolean,
            required: false
        }
    },
    components: {
        LoadingDialog,
        RetryDialog,
        SalesOrderPrintDialog,
        AlertDialog,
        SalesOrderExtendDialog,
        SalesOrderDialog,
        DeleteSingleDialog,
        DecisionDialog
    },
    data() {
        return {
            tax: 0,
            subtotal: 0,
            tota: 0,
            display: null,
            openParams: null,
            loading: {
                data: true,
            },
            loadingDialog: {
                message: "",
                display: false
            },
            selected: {
                lines: []
            },
            headers: {
                salesOrderLines: [
                    {text: this.$t("item"), value: "item"},
                    {text: this.$t("type"), value: "cast_type"},
                    {text: this.$t("quantity"), value: "quantity"},
                    {text: this.$t("price"), value: "price"},
                    {text: this.$t("sub_total"), value: "subtotal"},
                    {text: this.$t("tax"), value: "tax"},
                    {text: this.$t("total"), value: "total"},
                    {text: this.$t("delivered"), value: "delivered"},
                    {text: this.$t("extended"), value: "extended"},
                    {text: this.$t("remaining"), value: "remaining"},
                    {text: this.$t("available"), value: "available"}
                ]
            }
        }
    },
    watch: {
        "salesOrder.sales_order_item_lines": {
            handler() {
                this.calculateTotals();
            },
            deep: true
        }
    },
    computed: {
        ...mapState('sales/salesOrder', ["salesOrder"]),
        ...mapState('authentication', ["authorizations"]),
        hasSelectedLines() {
            return this.selected.lines.length > 0;
        },
        hasNoSelectedLines() {
            return !this.hasSelectedLines;
        }        
    },
    methods: {
        moment,
        formatPrice(amount) {
            return formatMoney(amount, "SDG ", 2);
        },
        formatNumber,
        ...mapActions("sales/salesOrder", ["fetchSalesOrder", "reopenSalesOrder", "removeSalesOrder", "releaseSalesOrder"]),
        open(params) {
            this.openParams = params;
            this.openDataFetch();
            this.display = true;
        },
        async openDataFetch() {
            try {
                this.loading.data = true;
                await this.fetchSalesOrder(this.openParams.id)
                this.loading.data = false;
            } catch(error) {
                let message = error.response ? error.response.data.message : error;
                this.$refs.retryDialog.open({message});

            }
        },
        openSalesOrderPrintDialog(params) {
            this.$refs.salesOrderPrintDialog.open(params);
        },
        onRetryDialogSubmit(params){
            if(params.target == "openDataFetch") {
                if(params.retry) this.createdDataFetch();
            }
        },
        reopenSalesOrderAction() {
            this.reopenSalesOrder(this.salesOrder.id)
                    .then(() => {
                        this.openDataFetch();
                        this.$emit("updated");
                        this.$refs.alertDialog.open({type: "success", message: this.$t("success")});
                    })
                    .catch((error) => {
                        let message = error.response ? error.response.data.message : error;
                        this.$refs.alertDialog.open({type: "error", message});
                    })
        },
        openSalesOrderExtendDialog() {
            this.$refs.salesOrderExtendDialog.open({id: this.salesOrder.id});
        },
        onSalesOrderExtendDialogSubmit() {
            this.openDataFetch();
            this.$emit("updated");
        },
        openSalesOrderDialog(params) {
            this.$refs.salesOrderDialog.open(params);
        },
        onSalesOrderDialogSubmit() {
            this.openDataFetch();
        },
        openDeleteDialog(params) {
            this.$refs.deleteSingleDialog.open(params);
        },
        onDeleteDialogSubmit(params) {
            if(params.target == 'salesOrder') {
                this.removeSalesOrder(params)
                    .then(() => {
                        this.$refs.deleteSingleDialog.close();
                        this.close();
                        this.$emit("updated");
                        this.$refs.alertDialog.open({type: "success", message: this.$t("records_delete_success")});
                    })
                    .catch((error) => {
                        this.$refs.deleteSingleDialog.close();
                        let message = error.response ? error.response.data.message : error;
                        this.$refs.alertDialog.open({type: "error", message});
                    })
            }
        },
        filterSalesOrder({id, date, validity_date, sales_order_item_lines, customer, customer_id}) {
            return {id, date, validity_date, sales_order_item_lines, customer, customer_id};
        },
        openDecisionDialog(params) {
            this.$refs.decisionDialog.open(params);
        },
        decisionDialogHandler(params) {
            switch (params.target) {
                case "extend_sales_order": this.extendSalesOrder(); break;
                case "extend_validity": this.openSalesOrderExtendDialog(); break;
                case "reopen_salesOrder_action": this.reopenSalesOrderAction(); break;
                case "release_salesOrder_action": this.releaseSalesOrderAction(); break;
                default: alert("Error in decision dialog!");
            }
        },
        extendSalesOrder() {
            let salesOrder = {};
            // salesOrder = JSON.parse(JSON.stringify(this.filter(this.salesOrder)));
            salesOrder = this.copyObject(this.filterSalesOrder(this.salesOrder));
            salesOrder.parent_id = salesOrder.id;
            salesOrder.id = null;
            salesOrder.notes = `This order is an extension for order # ${this.salesOrder.code}`;
            salesOrder.sales_order_item_lines.forEach(each => {
                each.quantity = each.remaining;
            });
            this.$refs.salesOrderDialog.open({salesOrder});
        },
        releaseSalesOrderAction() {
            this.openLoadingDialog({message: "Releasing Sales Order ..."})
            this.releaseSalesOrder({id: this.salesOrder.id})
                .then((response) => {
                    this.$emit("updated");
                    this.closeLoadingDialog();
                    this.$refs.alertDialog.open({ type: "success", message: response.data.message });
                    this.openDataFetch();
                })
                .catch(error => {
                    this.closeLoadingDialog();
                    this.$refs.deleteSingleDialog.close();
                    let message = error.response ? error.response.data.message : error;
                    this.$refs.alertDialog.open({type: "error", message});
                })
        },
        openLoadingDialog({message}) {
            this.loadingDialog.message = message;
            this.loadingDialog.display = true;
        },
        closeLoadingDialog() {
            this.loadingDialog.message = null;
            this.loadingDialog.display = false; 
        },
        close() {
            this.display = false;
        },
        emitSelect() {
            this.$emit("select", this.selected.lines[0])
        },
        calculateTotals() {
            this.tax = this.formatNumber(this.getTotalOf('tax'), 2);
            this.subtotal = this.formatNumber(this.getTotalOf('subtotal'), 2);
            this.total = this.formatPrice(this.getTotalOf('total'));
        },
        getTotalOf(field) {
            return this.salesOrder.sales_order_item_lines.reduce((total, line) =>  total += line[field] , 0);
        }
    },
}
</script>